import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import type { UserProfile } from '@/lib/partnerTypes';

export function usePartnerAuth() {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isPending, setIsPending] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    async function loadProfile() {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        if (mounted) { setProfile(null); setIsPending(false); setLoading(false); }
        return;
      }
      const { data } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();

      if (!mounted) return;

      if (!data) {
        setProfile(null);
        setIsPending(false);
        setLoading(false);
        return;
      }

      if (data.is_approved) {
        setProfile(data as UserProfile);
        setIsPending(false);
      } else {
        setProfile(null);
        setIsPending(true);
      }
      setLoading(false);
    }

    loadProfile();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      loadProfile();
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const logout = async () => {
    await supabase.auth.signOut();
    setProfile(null);
    setIsPending(false);
  };

  return { profile, isPending, loading, logout };
}
